import { ORPCError } from "@orpc/client";
import {
  type VisitInstructions,
  visitInstructionsSchema,
} from "@rosc/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ListChecks, Loader2, Undo2, X } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { toast } from "@/hooks/use-toast";
import { referenceClient } from "@/lib/orpc/client";
import { cn } from "@/lib/utils";
import { isInProgress, useVisitScope } from "@/lib/visit-queries";

const POLL_MS = 3000;
const dismissKey = (visitPublicId: string): string =>
  `rosc.recording-instructions.dismissed:${visitPublicId}`;
const readDismissed = (visitPublicId: string): boolean => {
  try {
    return sessionStorage.getItem(dismissKey(visitPublicId)) === "1";
  } catch {
    return false;
  }
};

/** Instructions typed during the recording, as the API applied them. */
export const useVisitInstructions = (
  visitPublicId: string | undefined,
  enabled: boolean,
) => {
  const scope = useVisitScope();
  return useQuery({
    queryKey: [
      "visit-instructions",
      scope?.uid,
      scope?.accountPublicId,
      scope?.generation,
      visitPublicId,
    ],
    enabled: enabled && !!scope && !!visitPublicId,
    retry: false,
    queryFn: async ({ signal }) =>
      visitInstructionsSchema.parse(
        await referenceClient.getVisitInstructions(
          { params: { public_id: String(visitPublicId) }, query: {} },
          { context: { expectedUserUid: scope?.uid }, signal },
        ),
      ),
    // Waiting for the note, then for the edit: a few seconds each.
    refetchInterval: (query) =>
      query.state.data?.state === "pending" ? POLL_MS : false,
  });
};

type NoticeProps = {
  visitPublicId: string | undefined;
  visitStatus: string | null | undefined;
  onNoteChanged: () => void;
};

/**
 * What the recording instructions changed in the note ("Applied: …"), so a
 * wrong shortcut match is easy to spot and undo. Nothing runs with
 * VITE_RECORDING_INSTRUCTIONS_ENABLED off.
 */
export const RecordingInstructionsNotice = (
  props: NoticeProps,
): JSX.Element | null =>
  AppConfig.enableRecordingInstructions && props.visitPublicId ? (
    <InstructionsNotice key={props.visitPublicId} {...props} />
  ) : null;

const InstructionsNotice = ({
  visitPublicId,
  visitStatus,
  onNoteChanged,
}: NoticeProps): JSX.Element | null => {
  const { t } = useTranslation();
  const scope = useVisitScope();
  const client = useQueryClient();
  const { data } = useVisitInstructions(visitPublicId, true);
  const [dismissed, setDismissed] = useState(() =>
    visitPublicId ? readDismissed(visitPublicId) : false,
  );
  // The note changed under the clinician's eyes: read it again.
  const previous = useRef<VisitInstructions["state"] | undefined>(undefined);
  useEffect(() => {
    if (previous.current === "pending" && data && data.state !== "pending")
      onNoteChanged();
    previous.current = data?.state;
  }, [data, onNoteChanged]);
  const undo = useMutation({
    mutationFn: async () =>
      visitInstructionsSchema.parse(
        await referenceClient.undoVisitInstructions(
          { params: { public_id: String(visitPublicId) }, query: {} },
          { context: { expectedUserUid: scope?.uid } },
        ),
      ),
    onSuccess: (result) => {
      client.setQueryData(
        [
          "visit-instructions",
          scope?.uid,
          scope?.accountPublicId,
          scope?.generation,
          visitPublicId,
        ],
        result,
      );
      onNoteChanged();
      toast({ variant: "success", title: t("recordingInstructions.undone") });
    },
    onError: (error) => {
      toast({
        variant: "error",
        title:
          error instanceof ORPCError && error.code === "CONFLICT"
            ? t("recordingInstructions.undoConflict")
            : t("recordingInstructions.undoFailed"),
      });
    },
  });

  if (!data || !visitPublicId || dismissed) return null;
  if (data.state === "pending") {
    if (!data.instructions.length) return null;
    const waiting = isInProgress(visitStatus);
    return (
      <p
        className="flex items-center gap-2.5 rounded-v3-panel bg-v3-surface-2 px-4 py-3 font-v3 text-[14px] text-v3-text-2 shadow-v3-hairline-strong"
        role="status"
      >
        <Loader2
          aria-hidden
          className="h-4 w-4 shrink-0 animate-spin text-v3-brand"
        />
        {waiting
          ? t("recordingInstructions.waiting", {
              count: data.instructions.length,
            })
          : t("recordingInstructions.applying")}
      </p>
    );
  }
  const { shortcuts, instructions } = data.applied;
  const applied = shortcuts.length + instructions.length > 0;
  if (!applied && !data.notApplied.length) return null;
  if (data.state === "undone") return null;
  const close = (): void => {
    try {
      sessionStorage.setItem(dismissKey(visitPublicId), "1");
    } catch {
      // Dismissing is a convenience; it still hides for this page view.
    }
    setDismissed(true);
  };
  const section = (name: string): string =>
    name.charAt(0).toUpperCase() + name.slice(1);

  return (
    <section
      aria-label={t("recordingInstructions.noticeLabel")}
      className="rounded-v3-panel bg-v3-surface-2 p-4 font-v3 text-v3-text shadow-v3-hairline-strong sm:px-5"
      data-testid="recording-instructions-notice"
    >
      <div className="flex items-center gap-2">
        <ListChecks
          aria-hidden
          className={cn(
            "h-4 w-4 shrink-0",
            applied ? "text-v3-brand" : "text-v3-text-3",
          )}
        />
        <p className="min-w-0 flex-1 text-[13px] font-medium text-v3-text-2">
          {applied
            ? t("recordingInstructions.appliedTitle")
            : t("recordingInstructions.noticeLabel")}
        </p>
        <button
          aria-label={t("recordingInstructions.dismiss")}
          className="-my-1.5 -mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-v3-text-3 transition-colors hover:bg-v3-surface-3 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
          type="button"
          onClick={close}
        >
          <X aria-hidden className="h-4 w-4" />
        </button>
      </div>
      {applied && (
        <ul className="mt-2.5 space-y-1.5 text-[15px] leading-[22px]">
          {shortcuts.map((shortcut) => (
            <li key={`s:${shortcut.name}`} className="flex gap-2.5">
              <span
                aria-hidden
                className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-v3-text-3"
              />
              <span className="min-w-0 break-words">
                {t("recordingInstructions.insertedShortcut", {
                  name: shortcut.name,
                })}
                {shortcut.section && (
                  <span className="ml-2 inline-flex h-[22px] items-center rounded-md bg-v3-panel px-1.5 align-[1px] text-[12px] font-medium text-v3-text-2 shadow-v3-hairline">
                    {section(shortcut.section)}
                  </span>
                )}
              </span>
            </li>
          ))}
          {instructions.map((item) => (
            <li key={`i:${item}`} className="flex gap-2.5">
              <span
                aria-hidden
                className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-v3-text-3"
              />
              <span className="min-w-0 break-words">{item}</span>
            </li>
          ))}
        </ul>
      )}
      {data.notApplied.length > 0 && (
        <div className="mt-3 flex gap-2.5 rounded-xl bg-v3-warn-tint px-3.5 py-3">
          <AlertTriangle
            aria-hidden
            className="mt-0.5 h-4 w-4 shrink-0 text-v3-warn"
          />
          <div className="min-w-0">
            <p className="text-[14px] font-semibold leading-5 text-v3-warn-text">
              {t("recordingInstructions.notAppliedTitle")}
            </p>
            <ul className="mt-0.5 space-y-1 text-[13.5px] leading-[19px] text-v3-text-2">
              {data.notApplied.map((item) => (
                <li key={`n:${item.text}`} className="break-words">
                  <span className="font-medium text-v3-text">{item.text}</span>
                  {" — "}
                  {item.reason === "fact_check"
                    ? t("recordingInstructions.reasonFactCheck")
                    : t("recordingInstructions.reasonUnavailable")}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {data.canUndo && (
        <div className="mt-3.5">
          <button
            className="inline-flex h-10 items-center gap-2 rounded-full bg-v3-panel px-4 text-[14px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:opacity-60"
            disabled={undo.isPending}
            type="button"
            onClick={() => {
              undo.mutate();
            }}
          >
            {undo.isPending ? (
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
            ) : (
              <Undo2 aria-hidden className="h-4 w-4" />
            )}
            {t("recordingInstructions.undo")}
          </button>
        </div>
      )}
    </section>
  );
};
